import { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Upload, Trash2, Loader2 } from 'lucide-react';
import { Button, Card, CardContent } from '@roger/ui-components';
import {
  client,
  getTaskDocuments,
  getTaskDocumentUrl,
  uploadTaskDocument,
  deleteTaskDocument,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';

interface TaskDocument {
  id: string;
  file_name: string;
}

interface TaskDocumentsSectionProps {
  taskId: string;
}

export default function TaskDocumentsSection({
  taskId,
}: TaskDocumentsSectionProps) {
  const [documents, setDocuments] = useState<TaskDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { error: showError } = useToast();

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getTaskDocuments({
        client,
        path: { task_id: taskId },
      });
      if (data) {
        setDocuments(data as TaskDocument[]);
      }
    } catch (err) {
      console.error('Failed to load task documents', err);
      showError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [taskId, showError]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileClick = async (docId: string) => {
    try {
      const { data } = await getTaskDocumentUrl({
        client,
        path: { task_id: taskId, document_id: docId },
      });
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get document URL', err);
      showError('Failed to open document. Please try again.');
    }
  };

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      await Promise.all(
        files.map(file =>
          uploadTaskDocument({
            client,
            path: { task_id: taskId },
            body: { file },
          })
        )
      );
      await fetchDocuments();
    } catch (err) {
      console.error('Failed to upload document', err);
      showError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    await uploadFiles(Array.from(selected));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId: string) => {
    setDeletingId(docId);
    try {
      await deleteTaskDocument({
        client,
        path: { task_id: taskId, document_id: docId },
      });
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      console.error('Failed to delete document', err);
      showError('Failed to delete document. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        uploadFiles(droppedFiles);
      }
    },
    [taskId, uploadFiles]
  );

  return (
    <Card>
      <CardContent className='space-y-2'>
        <span className='text-sm font-medium text-muted-foreground'>
          Documents
        </span>

        {/* Drag & Drop Upload Area */}
        <Card
          className={`border-2 border-dashed p-4 text-center transition-colors cursor-pointer ${
            isDragOver
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <div className='flex items-center justify-center gap-2 py-1'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
              <span className='text-sm text-muted-foreground'>
                Uploading...
              </span>
            </div>
          ) : (
            <>
              <Upload className='h-6 w-6 mx-auto mb-1.5 text-muted-foreground' />
              <p className='text-xs text-muted-foreground'>
                Drag & drop files here, or click to browse
              </p>
            </>
          )}
          <input
            ref={fileInputRef}
            type='file'
            className='hidden'
            onChange={handleUpload}
            accept='.pdf,.png,.jpg,.jpeg,.doc,.docx'
            multiple
          />
        </Card>

        {/* Document List */}
        {loading ? (
          <p className='px-2 py-3 text-center text-xs text-muted-foreground'>
            Loading...
          </p>
        ) : documents.length === 0 ? (
          <p className='px-2 py-3 text-center text-xs text-muted-foreground'>
            No documents attached
          </p>
        ) : (
          <div className='space-y-0.5'>
            {documents.map(doc => (
              <div
                key={doc.id}
                className='flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/50 group'
              >
                <button
                  className='flex items-center gap-2 flex-1 min-w-0 text-left'
                  onClick={() => handleFileClick(doc.id)}
                >
                  <FileText className='h-4 w-4 shrink-0 text-blue-500' />
                  <span className='truncate'>{doc.file_name}</span>
                </button>
                <Button
                  variant='ghost'
                  size='sm'
                  className='opacity-0 group-hover:opacity-100 h-7 w-7 p-0 text-muted-foreground hover:text-destructive'
                  onClick={() => handleDelete(doc.id)}
                  disabled={deletingId === doc.id}
                >
                  {deletingId === doc.id ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Trash2 className='h-3.5 w-3.5' />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
